import { Controller } from "@hotwired/stimulus"

const FILE_TYPE_ICONS = {
  "pdf": "📄",
  "doc": "📝", "docx": "📝",
  "xls": "📊", "xlsx": "📊", "csv": "📊",
  "ppt": "📎", "pptx": "📎",
  "zip": "🗜️", "rar": "🗜️", "7z": "🗜️", "tar": "🗜️", "gz": "🗜️",
  "txt": "📃", "md": "📃", "log": "📃",
  "json": "{ }", "xml": "📋", "yml": "📋", "yaml": "📋",
  "mp4": "🎬", "mov": "🎬", "avi": "🎬", "webm": "🎬",
  "mp3": "🎵", "wav": "🎵", "ogg": "🎵",
}

function getFileIcon(filename) {
  const ext = filename.split(".").pop().toLowerCase()
  return FILE_TYPE_ICONS[ext] || "📎"
}

function formatFileSize(bytes) {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default class extends Controller {
  static targets = [
    "description", "preview", "labelsInput", "writeTab", "previewTab",
    "fileInput", "fileCount", "filePreviewArea", "filePreviewList",
    "labelContainer"
  ]

  connect() {
    this.previewVisible = false
    this.selectedFiles = []
    this._selectedColor = "gray"
    this.syncSelectedLabels()
    this._setupLabelModal()
  }

  disconnect() {
    this._teardownLabelModal()
  }

  togglePreview() {
    this.previewVisible = !this.previewVisible

    if (this.hasWriteTabTarget && this.hasPreviewTabTarget) {
      this.writeTabTarget.classList.toggle("hidden", this.previewVisible)
      this.previewTabTarget.classList.toggle("hidden", !this.previewVisible)
    }

    if (this.previewVisible && this.hasDescriptionTarget && this.hasPreviewTarget) {
      this.previewTarget.textContent = this.descriptionTarget.value
    }
  }

  toggleLabel(event) {
    event.preventDefault()
    event.currentTarget.classList.toggle("selected")
    this.updateLabelsInput()
  }

  submit(event) {
    event.preventDefault()

    const form = event.target
    const formData = new FormData(form)
    
    if (this.hasFileInputTarget) {
      formData.delete(this.fileInputTarget.name)
      this.selectedFiles.forEach(file => {
        formData.append(this.fileInputTarget.name, file)
      })
    }

    const turboFrame = form.closest("turbo-frame")
    const headers = {
      "X-CSRF-Token": this.csrfToken,
      "Accept": "text/vnd.turbo-stream.html"
    }
    if (turboFrame) {
      headers["Turbo-Frame"] = turboFrame.id
    }

    fetch(form.action, {
      method: form.method.toUpperCase(),
      headers: headers,
      body: formData
    })
      .then(response => {
        if (response.redirected) {
          Turbo.visit(response.url)
          return null
        }
        return response.text()
      })
      .then(html => {
        if (html) {
          Turbo.renderStreamMessage(html)
        }
      })
  }

  closeModal() {
    const modalBackdrop = document.getElementById("issue-modal-backdrop")
    if (modalBackdrop?.contains(this.element)) {
      const controller = this.application.getControllerForElementAndIdentifier(modalBackdrop, "modal")
      controller?.close()
      return
    }

    const drawerBackdrop = document.getElementById("issue-drawer-backdrop")
    if (drawerBackdrop?.contains(this.element)) {
      const controller = this.application.getControllerForElementAndIdentifier(drawerBackdrop, "modal")
      controller?.close()
    }
  }

  updateLabelsInput() {
    if (!this.hasLabelsInputTarget) return

    const labels = this.element.querySelectorAll(".label-chip.selected")
    this.labelsInputTarget.value = Array.from(labels).map(chip => chip.dataset.label).join(",")
  }

  openLabelModal(event) {
    event.preventDefault()
    const modal = document.getElementById("label-creator-modal")
    if (!modal) return

    this._activeModal = modal
    this._selectedColor = "gray"

    const nameInput = document.getElementById("label-creator-name")
    if (nameInput) nameInput.value = ""

    this._renderSwatches()
    modal.classList.remove("hidden")
    setTimeout(() => nameInput?.focus(), 50)
  }

  closeLabelModal() {
    const modal = document.getElementById("label-creator-modal")
    if (!modal) return
    modal.classList.add("hidden")
    this._activeModal = null
    this._selectedColor = "gray"
  }

  _setupLabelModal() {
    const modal = document.getElementById("label-creator-modal")
    if (!modal) return

    this._onModalBackdropClick = (e) => { if (e.target === modal) this.closeLabelModal() }
    this._onCloseClick = () => this.closeLabelModal()
    this._onCancelClick = () => this.closeLabelModal()
    this._onConfirmClick = () => this.confirmLabelCreation()
    this._onNameKeydown = (e) => {
      if (e.key === "Enter") { e.preventDefault(); this.confirmLabelCreation() }
      else if (e.key === "Escape") this.closeLabelModal()
    }

    modal.addEventListener("click", this._onModalBackdropClick)
    document.getElementById("label-creator-close")?.addEventListener("click", this._onCloseClick)
    document.getElementById("label-creator-cancel")?.addEventListener("click", this._onCancelClick)
    document.getElementById("label-creator-confirm")?.addEventListener("click", this._onConfirmClick)
    document.getElementById("label-creator-name")?.addEventListener("keydown", this._onNameKeydown)
  }

  _teardownLabelModal() {
    const modal = document.getElementById("label-creator-modal")
    if (!modal) return

    modal.removeEventListener("click", this._onModalBackdropClick)
    document.getElementById("label-creator-close")?.removeEventListener("click", this._onCloseClick)
    document.getElementById("label-creator-cancel")?.removeEventListener("click", this._onCancelClick)
    document.getElementById("label-creator-confirm")?.removeEventListener("click", this._onConfirmClick)
    document.getElementById("label-creator-name")?.removeEventListener("keydown", this._onNameKeydown)

    if (this._activeModal) this.closeLabelModal()
  }

  _renderSwatches() {
    const container = document.getElementById("label-creator-swatches")
    if (!container) return

    const colors = ["red", "blue", "green", "purple", "orange", "pink", "yellow", "gray"]
    const hexMap = {
      red: "#ef4444", blue: "#3b82f6", green: "#22c55e", purple: "#a855f7",
      orange: "#f97316", pink: "#ec4899", yellow: "#eab308", gray: "#9ca3af"
    }

    container.innerHTML = ""
    colors.forEach(color => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.title = color.charAt(0).toUpperCase() + color.slice(1)
      const isSelected = color === this._selectedColor
      btn.className = `cursor-pointer w-6 h-6 rounded-full ring-2 ring-offset-2 dark:ring-offset-gray-900 hover:scale-110 transition-all duration-150 ${isSelected ? "ring-indigo-500" : "ring-transparent"}`
      btn.style.backgroundColor = hexMap[color]
      btn.addEventListener("click", () => {
        this._selectedColor = color
        container.querySelectorAll("button").forEach(b => {
          const sel = b.title.toLowerCase() === color
          b.classList.toggle("ring-indigo-500", sel)
          b.classList.toggle("ring-transparent", !sel)
        })
      })
      container.appendChild(btn)
    })
  }

  async confirmLabelCreation() {
    const nameInput = document.getElementById("label-creator-name")
    if (!nameInput) return
    const name = nameInput.value.trim()
    if (!name) return

    const existing = Array.from(this.element.querySelectorAll(".label-chip[data-label]"))
      .find(chip => chip.dataset.label.toLowerCase() === name.toLowerCase())

    if (existing) {
      existing.classList.add("selected")
      this.closeLabelModal()
      this.updateLabelsInput()
      return
    }

    try {
      const response = await fetch("/issue_labels", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ issue_label: { name, color: this._selectedColor || "gray" } })
      })

      if (!response.ok) return

      const { name: savedName, color: savedColor } = await response.json()
      this._addChip(savedName, savedColor)
      this.closeLabelModal()
    } catch (_) {
      // network error — fail silently, user can retry
    }
  }

  _addChip(name, color) {
    const chip = document.createElement("button")
    chip.type = "button"
    chip.dataset.label = name
    chip.dataset.color = color
    chip.dataset.action = "issue-form#toggleLabel"
    chip.className = `label-chip px-2.5 py-1 text-xs font-medium rounded-full border transition-colors selected ${this._colorClasses(color)}`
    chip.textContent = name

    if (this.hasLabelContainerTarget) {
      const trigger = this.labelContainerTarget.querySelector("[data-action='issue-form#openLabelModal']")
      trigger
        ? this.labelContainerTarget.insertBefore(chip, trigger)
        : this.labelContainerTarget.appendChild(chip)
    }

    this.updateLabelsInput()
  }

  _colorClasses(color) {
    const map = {
      red:    "bg-red-50 text-red-700 border-red-200 [&.selected]:bg-red-100 [&.selected]:border-red-400 dark:bg-red-950/40 dark:text-red-400 dark:border-red-900/50 dark:[&.selected]:bg-red-900/60 dark:[&.selected]:border-red-500",
      blue:   "bg-blue-50 text-blue-700 border-blue-200 [&.selected]:bg-blue-100 [&.selected]:border-blue-400 dark:bg-blue-950/40 dark:text-blue-400 dark:border-blue-900/50 dark:[&.selected]:bg-blue-900/60 dark:[&.selected]:border-blue-500",
      green:  "bg-green-50 text-green-700 border-green-200 [&.selected]:bg-green-100 [&.selected]:border-green-400 dark:bg-green-950/40 dark:text-green-400 dark:border-green-900/50 dark:[&.selected]:bg-green-900/60 dark:[&.selected]:border-green-500",
      purple: "bg-purple-50 text-purple-700 border-purple-200 [&.selected]:bg-purple-100 [&.selected]:border-purple-400 dark:bg-purple-950/40 dark:text-purple-400 dark:border-purple-900/50 dark:[&.selected]:bg-purple-900/60 dark:[&.selected]:border-purple-500",
      orange: "bg-orange-50 text-orange-700 border-orange-200 [&.selected]:bg-orange-100 [&.selected]:border-orange-400 dark:bg-orange-950/40 dark:text-orange-400 dark:border-orange-900/50 dark:[&.selected]:bg-orange-900/60 dark:[&.selected]:border-orange-500",
      pink:   "bg-pink-50 text-pink-700 border-pink-200 [&.selected]:bg-pink-100 [&.selected]:border-pink-400 dark:bg-pink-950/40 dark:text-pink-400 dark:border-pink-900/50 dark:[&.selected]:bg-pink-900/60 dark:[&.selected]:border-pink-500",
      yellow: "bg-yellow-50 text-yellow-700 border-yellow-200 [&.selected]:bg-yellow-100 [&.selected]:border-yellow-400 dark:bg-yellow-950/40 dark:text-yellow-400 dark:border-yellow-900/50 dark:[&.selected]:bg-yellow-900/60 dark:[&.selected]:border-yellow-500",
      gray:   "bg-gray-50 text-gray-700 border-gray-200 [&.selected]:bg-gray-100 [&.selected]:border-gray-400 dark:bg-gray-800/40 dark:text-gray-300 dark:border-gray-700 dark:[&.selected]:bg-gray-700 dark:[&.selected]:border-gray-500"
    }
    return map[color] || map.gray
  }

  syncSelectedLabels() {
    if (!this.hasLabelsInputTarget) return

    const selected = this.labelsInputTarget.value.split(",").map(label => label.trim()).filter(Boolean)
    this.element.querySelectorAll(".label-chip").forEach(chip => {
      chip.classList.toggle("selected", selected.includes(chip.dataset.label))
    })
  }

  previewFiles() {
    if (!this.hasFileInputTarget) return
    
    const newFiles = Array.from(this.fileInputTarget.files)
    this.selectedFiles.push(...newFiles)

    this._renderFilePreviews()
  }

  removeFile(event) {
    event.preventDefault()
    const index = parseInt(event.currentTarget.dataset.index, 10)
    this.selectedFiles.splice(index, 1)
    this._renderFilePreviews()
  }

  _renderFilePreviews() {
    const list = this.filePreviewListTarget
    const area = this.filePreviewAreaTarget

    list.innerHTML = ""

    if (this.selectedFiles.length === 0) {
      area.classList.add("hidden")
      this.fileCountTarget.textContent = ""
      return
    }

    area.classList.remove("hidden")
    this.fileCountTarget.textContent = `${this.selectedFiles.length} file${this.selectedFiles.length === 1 ? "" : "s"} selected`

    this.selectedFiles.forEach((file, index) => {
      if (file.type.startsWith("image/")) {
        this._renderImagePreview(file, index, list)
      } else {
        this._renderFileCard(file, index, list)
      }
    })
  }

  _createRemoveBtn(index) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.index = index
    btn.dataset.action = "issue-form#removeFile"
    btn.className = "absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-red-500 hover:bg-red-600 text-white flex items-center justify-center text-xs leading-none shadow-sm transition-colors cursor-pointer"
    btn.innerHTML = "×"
    return btn
  }

  _renderImagePreview(file, index, container) {
    const reader = new FileReader()
    reader.onload = (e) => {
      const wrapper = document.createElement("div")
      wrapper.className = "relative group/thumb"

      const img = document.createElement("img")
      img.src = e.target.result
      img.className = "w-20 h-20 object-cover rounded-lg border border-gray-200 dark:border-gray-700"
      img.alt = file.name

      const nameTag = document.createElement("span")
      nameTag.className = "block text-[10px] text-gray-400 dark:text-gray-500 truncate w-20 mt-0.5"
      nameTag.textContent = file.name

      wrapper.appendChild(img)
      wrapper.appendChild(this._createRemoveBtn(index))
      wrapper.appendChild(nameTag)
      container.appendChild(wrapper)
    }
    reader.readAsDataURL(file)
  }

  _renderFileCard(file, index, container) {
    const wrapper = document.createElement("div")
    wrapper.className = "relative flex items-center gap-2 px-2.5 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800"

    const icon = document.createElement("span")
    icon.className = "text-base shrink-0"
    icon.textContent = getFileIcon(file.name)

    const info = document.createElement("div")
    info.className = "min-w-0 flex-1"

    const name = document.createElement("span")
    name.className = "block text-xs font-medium text-gray-700 dark:text-gray-300 truncate max-w-[140px]"
    name.textContent = file.name

    const size = document.createElement("span")
    size.className = "block text-[10px] text-gray-400 dark:text-gray-500"
    size.textContent = formatFileSize(file.size)

    info.appendChild(name)
    info.appendChild(size)
    wrapper.appendChild(icon)
    wrapper.appendChild(info)
    wrapper.appendChild(this._createRemoveBtn(index))
    container.appendChild(wrapper)
  }

  get csrfToken() {
    return document.querySelector('meta[name="csrf-token"]').content
  }
}
